import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import { AnimatedLogo } from '@experian-uk/corvetteuk-common-ui';
import ProductCard from '../../components/ProductCard';
import OrDivider from '../../components/OrDivider';
import { ChooserPageContainer, ContentContainer, Heading, Text, CardsContainer } from './index.styles';
import { FullPageContainer } from '../../components/Container';
import { getEnv } from '../../components/Context/env';
import usePageView from '../../hooks/usePageView';
import { trackFreeAccountCtaClick, trackRestartCtaClick } from '../../helpers/chooser/analytics';
import usePreviousSubscriptionFamily from '../../hooks/usePreviousSubscriptionFamily';
import { products, stateVariables } from '../../constants';
import { createSubscriptionConfig } from './constants';
import getSubscriptionPriceFromOffers from '../../reducers/selectors/getSubscriptionPriceFromOffers';
import findFlexPlanByFamily from '../../helpers/flex-helpers/findFlexPlanByFamily';
import { normalizeProductFamily } from '../../constants/products';
import { useGetEntitlementsQuery, useGetIsFlexUserQuery, useGetAllProductsQuery } from '../../services/entitlementsApi';

const PAGE_NAME = 'ProdMoveChooserPage';

const ChooserPage = () => {
  const env = getEnv();
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    data: flexEligibleData,
    isLoading: isFlexEligibleLoading,
    isUninitialized: isFlexEligibleUninitialized,
  } = useGetIsFlexUserQuery();

  const isFlexEligibilityResolved = !isFlexEligibleUninitialized && !isFlexEligibleLoading;
  const isFlex = isFlexEligibilityResolved && flexEligibleData?.isFlex === true;

  const { data: entitlementsData, isLoading: isEntitlementsLoading } = useGetEntitlementsQuery(undefined, {
    skip: !isFlex,
  });
  const { data: allProductsData, isLoading: isProductsLoading } = useGetAllProductsQuery(undefined, {
    skip: !isFlex,
  });

  const freeOfferId = useSelector(state => state.offers?.basic?.offerId);
  const { userPreviousSubscriptionFamily, isActiveUser: oldIsActiveUser } = usePreviousSubscriptionFamily();
  const identityPrice = useSelector(getSubscriptionPriceFromOffers(products.identity));
  const creditExpertPrice = useSelector(getSubscriptionPriceFromOffers(products.credit_expert));

  const flexPlanCode = entitlementsData?.plans?.[0]?.planCode;
  const flexFamily = normalizeProductFamily(flexPlanCode);
  const flexIsActiveUser = entitlementsData?.active === true;
  const flexIdentityPrice = findFlexPlanByFamily(
    products.identity,
    allProductsData?.eligiblePlans,
    allProductsData?.isTrialEligible
  )?.price;
  const flexCreditExpertPrice = findFlexPlanByFamily(
    products.credit_expert,
    allProductsData?.eligiblePlans,
    allProductsData?.isTrialEligible
  )?.price;

  const activeFamily = isFlex ? flexFamily : userPreviousSubscriptionFamily;
  const isActiveUser = isFlex ? flexIsActiveUser : oldIsActiveUser;
  const effectiveIdentityPrice = isFlex ? flexIdentityPrice : identityPrice;
  const effectiveCreditExpertPrice = isFlex ? flexCreditExpertPrice : creditExpertPrice;

  const isFlexDataLoading = isFlex && (isEntitlementsLoading || isProductsLoading);
  const isPageLoading = !isFlexEligibilityResolved || isFlexDataLoading;

  const subscriptionFamilyDisplayNames = {
    identity: 'Identity Plus',
    credit_expert: 'CreditExpert',
  };

  const userPreviousSubscriptionFamilyDisplay = subscriptionFamilyDisplayNames[activeFamily];
  const SUBSCRIPTION_CONFIG = createSubscriptionConfig(effectiveIdentityPrice, effectiveCreditExpertPrice);

  const redirectToHome = () =>
    dispatch(
      push('/', {
        [stateVariables.persistErrorsOnLocationChange]: true,
      })
    );

  const getPaidSubscriptionCardData = () => {
    switch (activeFamily) {
      case products.identity:
        return SUBSCRIPTION_CONFIG.identity;
      case products.credit_expert:
        return SUBSCRIPTION_CONFIG.credit_expert;
      default:
        return undefined;
    }
  };

  const handleSubscriptionClick = () => {
    trackRestartCtaClick(activeFamily);
    history.push(`/chooser/restart?product=${activeFamily}`);
  };

  const paidSubscriptionData = getPaidSubscriptionCardData();

  useEffect(() => {
    if (!isPageLoading && isActiveUser) {
      redirectToHome();
    }
  }, [isPageLoading, isActiveUser]);

  usePageView(PAGE_NAME, {
    chooser: { previousPaidSubscription: activeFamily },
  });

  if (isPageLoading) {
    return (
      <FullPageContainer data-automation-test-element="chooser-loading">
        <AnimatedLogo />
      </FullPageContainer>
    );
  }

  return (
    <ThemeProvider theme={nebulaTheme}>
      <ChooserPageContainer>
        <ContentContainer>
          <Heading>Welcome Back</Heading>
          <Text>It looks like you&apos;ve had an Experian account before.</Text>
          <Text bold>Take a moment to review our products and choose the one that fits you best.</Text>

          <CardsContainer>
            <ProductCard
              productConfig={SUBSCRIPTION_CONFIG.free}
              buttonVariant="secondary"
              href={`${env.REACT_APP_C2_URL}/auth/welcome?offerId=${freeOfferId}&brand=creditmatcher`}
              onButtonClick={trackFreeAccountCtaClick}
            />

            {paidSubscriptionData && (
              <React.Fragment>
                <OrDivider />
                <ProductCard
                  productConfig={paidSubscriptionData}
                  priceText={paidSubscriptionData.priceText}
                  onButtonClick={handleSubscriptionClick}
                  isPaidSubscriptionCard
                  previousSubscriptionFamily={userPreviousSubscriptionFamilyDisplay}
                />
              </React.Fragment>
            )}
          </CardsContainer>
        </ContentContainer>
      </ChooserPageContainer>
    </ThemeProvider>
  );
};

export default ChooserPage;
